import React from 'react';
import { useDroppable } from '@dnd-kit/core';
import { ColumnLayoutElement as ColumnLayoutType } from '../../types/element.types';
import CanvasElement from '../Canvas/CanvasElement';

interface ColumnLayoutElementProps {
  element: ColumnLayoutType;
  sectionId: string;
  columnId: string;
}

/**
 * Column Layout Element Renderer
 * Creates multiple droppable columns for nested content
 */
const ColumnLayoutElement: React.FC<ColumnLayoutElementProps> = ({ element, sectionId, columnId }) => {
  const gap = element.settings?.gap || '20px';
  const nestedColumns = element.content?.columns || [];

  // Ensure we have the right number of columns
  const columnsCount = element.settings?.columns || 2;

  return (
    <div
      className="column-layout-element w-full"
      style={{
        display: 'flex',
        gap: gap,
      }}
    >
      {nestedColumns.slice(0, columnsCount).map((nestedColumn, index) => (
        <NestedColumn
          key={nestedColumn.id}
          nestedColumn={nestedColumn}
          parentElementId={element.id}
          sectionId={sectionId}
          columnId={columnId}
          index={index}
        />
      ))}
    </div>
  );
};

/**
 * Nested Column Component
 * A droppable zone within a column layout element
 */
interface NestedColumnProps {
  nestedColumn: { id: string; elements: any[] };
  parentElementId: string;
  sectionId: string;
  columnId: string;
  index: number;
}

const NestedColumn: React.FC<NestedColumnProps> = ({
  nestedColumn,
  parentElementId,
  sectionId,
  columnId,
  index,
}) => {
  const { setNodeRef, isOver } = useDroppable({
    id: `nested-column-${nestedColumn.id}`,
    data: {
      type: 'nested-column',
      nestedColumnId: nestedColumn.id,
      parentElementId: parentElementId,
      sectionId: sectionId,
      columnId: columnId,
    },
  });

  return (
    <div
      ref={setNodeRef}
      className={`
        flex-1 rounded-lg transition-all duration-200
        ${isOver
          ? 'bg-blue-100 border-2 border-dashed border-blue-500 shadow-inner'
          : 'border-2 border-dashed border-gray-300 hover:border-gray-400'
        }
      `}
      style={{
        minHeight: '120px',
        padding: '12px',
      }}
    >
      {nestedColumn.elements.length === 0 ? (
        <div
          className={`
            flex flex-col items-center justify-center h-full text-xs rounded-md p-4
            transition-all duration-200
            ${isOver
              ? 'bg-blue-50 text-blue-700'
              : 'text-gray-400 hover:text-gray-600'
            }
          `}
        >
          <div className="text-2xl mb-1">
            {isOver ? '⬇️' : '📦'}
          </div>
          <div className="font-medium">
            {isOver ? 'Drop here!' : `Nested Column ${index + 1}`}
          </div>
          {!isOver && (
            <div className="text-xs mt-1 text-gray-400">
              Drag elements here
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {nestedColumn.elements.map((nestedElement: any) => (
            <CanvasElement
              key={nestedElement.id}
              element={nestedElement}
              sectionId={sectionId}
              columnId={columnId}
            />
          ))}
          {isOver && (
            <div className="h-12 border-2 border-dashed border-blue-500 bg-blue-50 rounded-md flex items-center justify-center text-blue-600 text-xs animate-pulse">
              Drop to add
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ColumnLayoutElement;

